<template>
  <div v-if="shouldShowToggle" class="relative">
    <div class="top-[-18px] flex gap-px rounded-md border border-gray-300 bg-white p-0.5">
      <button
        type="button"
        :class="getButtonClasses(BACKGROUND_TYPES.CHECKERED)"
        class="flex cursor-pointer items-center justify-center rounded border-0 p-1.5 transition-all duration-150 ease-in-out"
        title="Checkered background"
        @click="handleBackgroundChange(BACKGROUND_TYPES.CHECKERED)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="h-4 w-4"
          :class="{
            'text-white': modelValue === BACKGROUND_TYPES.CHECKERED
          }"
        >
          <rect width="18" height="18" x="3" y="3" rx="2" />
          <path d="M3 9h18" />
          <path d="M3 15h18" />
          <path d="M9 3v18" />
          <path d="M15 3v18" />
        </svg>
      </button>
      <button
        type="button"
        :class="getButtonClasses(BACKGROUND_TYPES.GREEN)"
        class="flex cursor-pointer items-center justify-center rounded border-0 p-1.5 transition-all duration-150 ease-in-out"
        title="Green screen background"
        @click="handleBackgroundChange(BACKGROUND_TYPES.GREEN)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="h-4 w-4"
          :class="{
            'text-white': modelValue === BACKGROUND_TYPES.GREEN
          }"
        >
          <rect width="18" height="18" x="3" y="3" rx="2" />
        </svg>
      </button>
      <button
        v-if="isFaceliftExperimentActive"
        type="button"
        class="background-toggle__learn-more-icon flex cursor-pointer items-center justify-center rounded border-0 bg-transparent p-1.5 transition-all duration-150 ease-in-out hover:bg-gray-100"
        :class="{ 'bg-gray-100': showTooltip }"
        title="Learn more about backgrounds"
        @click="toggleTooltip"
      >
        <UseSvg icon="question-circle" class="h-4 w-4 text-gray-700" />
      </button>
    </div>
    <div v-if="showTooltip" class="background-toggle__tooltip">
      <div class="background-toggle__tooltip-content">
        <button type="button" class="background-toggle__tooltip-close" @click="closeTooltip">
          <UseSvg icon="modal-close" class="h-4 w-4" />
        </button>
        <h3 class="background-toggle__tooltip-title">Transparency check</h3>
        <p class="background-toggle__tooltip-text">
          Toggle backgrounds to check transparency and edges. The checkered pattern reveals any
          see-through areas, while solid green shows edge quality.
        </p>
        <img
          src="https://jiffy.imgix.net/gen/pdp/jiffy-transparancy-anim.gif?auto=format"
          alt="Transparency check example"
          class="background-toggle__tooltip-image"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { track } from "jiffy-analytics";
import { computed, defineEmits, defineProps, ref } from "vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { useDtfStore } from "../store";
import { BACKGROUND_TYPES } from "./constants";

const props = defineProps({
  modelValue: {
    type: String,
    default: "checkered",
    validator(value) {
      return ["checkered", "green"].includes(value);
    }
  },
  onLearnMore: {
    type: Function,
    default: null
  },
  forceShow: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:modelValue"]);

const store = useDtfStore();
const showTooltip = ref(false);

const isFaceliftExperimentActive = computed(() => store.isFaceliftExperimentActive);

const toggleTooltip = () => {
  showTooltip.value = !showTooltip.value;
};

const closeTooltip = () => {
  showTooltip.value = false;
};

const shouldShowToggle = computed(() => props.forceShow || store.catalogNumber === "DTF001");

const BUTTON_CLASSES = {
  ACTIVE: "bg-black text-white hover:bg-black",
  INACTIVE: "bg-transparent hover:bg-gray-100"
};

const getButtonClasses = value =>
  props.modelValue === value ? BUTTON_CLASSES.ACTIVE : BUTTON_CLASSES.INACTIVE;

const ANALYTICS_MODE_MAP = {
  [BACKGROUND_TYPES.GREEN]: "green screen",
  [BACKGROUND_TYPES.CHECKERED]: "chessboard"
};

const handleBackgroundChange = backgroundType => {
  track("image_preview_switcher_clicked", {
    page_type: "transfers_pdp",
    mode: ANALYTICS_MODE_MAP[backgroundType] || "chessboard",
    timestamp: Date.now()
  });
  emit("update:modelValue", backgroundType);
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";

.background-toggle {
  &__tooltip {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    z-index: 100;
    width: 280px;
  }

  &__tooltip-content {
    background-color: $white;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    position: relative;
  }

  &__tooltip-close {
    position: absolute;
    top: 8px;
    right: 8px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.1);
    }
  }

  &__tooltip-title {
    font-size: 16px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: $mirage;
  }

  &__tooltip-text {
    font-size: 14px;
    line-height: 1.5;
    margin: 0 0 12px 0;
    color: $crafterpay-gray-modern-600;
    padding-right: 20px;
  }

  &__tooltip-image {
    width: 100%;
    height: auto;
    border-radius: 4px;
    display: block;
  }
}
</style>
